import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Typography,
  Box,
  Card,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  LockReset as LockResetIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  AdminPanelSettings as AdminIcon,
  RocketLaunch as BoostIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';

const UserManagement = () => {
  const { apiCall, currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userStats, setUserStats] = useState({
    total: 0,
    jobseekers: 0,
    employers: 0,
    serviceProviders: 0,
    admins: 0,
    active: 0,
    blocked: 0
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/users', {
        method: 'GET'
      });

      if (response.success) {
        setUsers(response.users || []);
        calculateUserStats(response.users || []);
      } else {
        // Fallback to mock data if API fails
        const mockUsers = [
          {
            id: 1,
            firstName: 'John',
            lastName: 'Doe',
            email: 'john@example.com',
            role: 'jobseeker',
            status: 'active',
            createdAt: '2023-01-15T00:00:00Z',
            lastLogin: '2024-01-10T10:30:00Z'
          },
          {
            id: 2,
            firstName: 'Jane',
            lastName: 'Smith',
            email: 'jane@example.com',
            role: 'employer',
            status: 'active',
            createdAt: '2023-02-20T00:00:00Z',
            lastLogin: '2024-01-09T15:45:00Z'
          },
          {
            id: 3,
            firstName: 'Mike',
            lastName: 'Johnson',
            email: 'mike@example.com',
            role: 'serviceprovider',
            status: 'blocked',
            createdAt: '2023-03-10T00:00:00Z',
            lastLogin: '2024-01-05T09:20:00Z'
          },
          {
            id: 4,
            firstName: 'Admin',
            lastName: 'User',
            email: 'admin@jobit.com',
            role: 'admin',
            status: 'active',
            createdAt: '2023-06-15T00:00:00Z',
            lastLogin: '2024-01-11T08:00:00Z'
          },
          {
            id: 5,
            firstName: 'Sarah',
            lastName: 'Wilson',
            email: 'sarah@example.com',
            role: 'jobseeker',
            status: 'active',
            createdAt: '2023-06-01T00:00:00Z',
            lastLogin: '2024-01-08T14:30:00Z'
          }
        ];
        setUsers(mockUsers);
        calculateUserStats(mockUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const calculateUserStats = (userList) => {
    const stats = userList.reduce((acc, user) => {
      acc.total++;
      acc[user.role === 'serviceprovider' ? 'serviceProviders' : user.role + 's']++;
      acc[user.status]++;
      return acc;
    }, {
      total: 0,
      jobseekers: 0,
      employers: 0,
      serviceProviders: 0,
      admins: 0,
      active: 0,
      blocked: 0
    });
    setUserStats(stats);
  };

  const handleOpenDialog = (user = null, edit = false) => {
    setSelectedUser(user || {
      firstName: '',
      lastName: '',
      email: '',
      role: 'jobseeker',
      status: 'active',
    });
    setEditMode(edit);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setEditMode(false);
  };

  const handleSaveUser = async () => {
    try {
      if (editMode) {
        const response = await apiCall(`/admin/users/${selectedUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(selectedUser)
        });
        
        if (response.success) {
          setUsers(users.map(user => 
            user.id === selectedUser.id ? { ...user, ...selectedUser } : user
          ));
        }
      } else {
        const response = await apiCall('/admin/users', {
          method: 'POST',
          body: JSON.stringify(selectedUser)
        });
        
        if (response.success) {
          const newUser = {
            ...selectedUser,
            id: Date.now(),
            createdAt: new Date().toISOString(),
            lastLogin: null
          };
          setUsers([...users, newUser]);
        }
      }
      handleCloseDialog();
      calculateUserStats(users);
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await apiCall(`/admin/users/${id}`, {
          method: 'DELETE'
        });
        
        if (response.success) {
          const updatedUsers = users.filter(user => user.id !== id);
          setUsers(updatedUsers);
          calculateUserStats(updatedUsers);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Failed to delete user');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const user = users.find(u => u.id === id);
      const newStatus = user.status === 'active' ? 'blocked' : 'active';
      
      const response = await apiCall(`/admin/users/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.success) {
        const updatedUsers = users.map(user => 
          user.id === id ? { ...user, status: newStatus } : user
        );
        setUsers(updatedUsers);
        calculateUserStats(updatedUsers);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Failed to update user status');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordDialog(true);
  };

  const handleConfirmPasswordReset = async () => {
    try {
      const userId = selectedUser?.id || selectedUser?._id;
      const response = await apiCall(`/admin/users/${userId}/reset-password`, {
        method: 'POST',
        // Send both keys for maximum backend compatibility
        body: JSON.stringify({ newPassword, password: newPassword })
      });
      
      if (response.success) {
        alert(`Password for ${selectedUser.email} has been reset successfully.`);
      }
      
      setResetPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      const msg = (error && error.message) ? error.message : 'Failed to reset password. Please try again.';
      alert(msg);
    }
  };

  const handleBoostUser = async (user) => {
    try {
      const uid = user?.id || user?._id;
      // Admins can override payment requirement when boosting
      await ApiService.boostUserProfile(uid, { override: true });
      alert(`Boosted profile for ${user.email || uid} successfully.`);
    } catch (error) {
      console.error('Error boosting user profile:', error);
      alert('Failed to boost user profile.');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleIcon = (role) => {
    switch (role) {
      case 'jobseeker': return <PersonIcon />;
      case 'employer': return <WorkIcon />;
      case 'serviceprovider': return <BuildIcon />;
      case 'admin': return <AdminIcon />;
      default: return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'jobseeker': return 'primary';
      case 'employer': return 'success';
      case 'serviceprovider': return 'warning';
      case 'admin': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getRoleIcon(params.row.role)}
          <Typography variant="body2">
            {`${params.row.firstName || ''} ${params.row.lastName || ''}`.trim()}
          </Typography>
        </Box>
      )
    },
    { field: 'email', headerName: 'Email', flex: 1 },
    { 
      field: 'role', 
      headerName: 'Role', 
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={getRoleColor(params.value)}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'active' ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { 
      field: 'createdAt', 
      headerName: 'Join Date', 
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    { 
      field: 'lastLogin', 
      headerName: 'Last Login', 
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Boost Profile">
            <IconButton
              onClick={() => handleBoostUser(params.row)}
              size="small"
              color="secondary"
            >
              <BoostIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit User">
            <IconButton
              onClick={() => handleOpenDialog(params.row, true)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Password">
            <IconButton
              onClick={() => handleResetPassword(params.row)}
              size="small"
              color="warning"
            >
              <LockResetIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'active' ? 'Block User' : 'Unblock User'}>
            <IconButton
              onClick={() => handleToggleStatus(params.row.id)}
              size="small"
              color={params.row.status === 'active' ? 'error' : 'success'}
            >
              {params.row.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton
              onClick={() => handleDeleteUser(params.row.id)}
              size="small"
              color="error"
              disabled={
                // Super admin has unrestricted access (GodMode)
                currentUser?.role === 'super_admin' 
                  ? false 
                  : params.row.role === 'admin' && params.row.id === currentUser?.id
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadUsers} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* User Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{userStats.total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Users</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{userStats.jobseekers}</Typography>
            <Typography variant="body2" color="text.secondary">Job Seekers</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{userStats.employers}</Typography>
            <Typography variant="body2" color="text.secondary">Employers</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{userStats.serviceProviders}</Typography>
            <Typography variant="body2" color="text.secondary">Service Providers</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">{userStats.admins}</Typography>
            <Typography variant="body2" color="text.secondary">Admins</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{userStats.active}</Typography>
            <Typography variant="body2" color="text.secondary">Active</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="Role Filter"
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="jobseeker">Job Seekers</MenuItem>
                <MenuItem value="employer">Employers</MenuItem>
                <MenuItem value="serviceprovider">Service Providers</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredUsers.length} of {users.length} users
            </Typography>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          autoHeight
          disableSelectionOnClick
          sx={{ border: 0 }}
        />
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={selectedUser?.firstName || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, firstName: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={selectedUser?.lastName || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, lastName: e.target.value })}
                  margin="normal"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={selectedUser?.email || ''}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={selectedUser?.role || 'jobseeker'}
              onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
              margin="normal"
            >
              <MenuItem value="jobseeker">Job Seeker</MenuItem>
              <MenuItem value="employer">Employer</MenuItem>
              <MenuItem value="serviceprovider">Service Provider</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label="Status"
              value={selectedUser?.status || 'active'}
              onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
              margin="normal"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editMode ? 'Save Changes' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {selectedUser?.firstName} {selectedUser?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new password for {selectedUser?.email}
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            placeholder="Enter new password"
            helperText="Password should be at least 8 characters long"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmPasswordReset} 
            variant="contained" 
            color="warning"
            disabled={!newPassword.trim() || newPassword.length < 8}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;